import type { IContextMutationResult } from '@modules/core/services/query/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';

import type { IHistoryOfTrackerGroupsChanges, ITrackerGroupsFilterResponse, ITrackerGroupsPayload, ITrackerGroupsSaveResponse } from '../types';
import {
  AFFILIATE_SAVE_TRACKER_GROUPS_API_URL,
  AFFILIATE_SAVE_TRACKER_GROUPS_MUTATION,
  AFFILIATE_SHOW_HISTORY_TRACKER_GROUPS_QUERY,
  AFFILIATE_SHOW_TRACKER_GROUP_ITEM_QUERY,
  AFFILIATES_TRACKER_GROUPS_PERMISSIONS,
} from '../constants';

export default function useSaveTrackerGroupsMutation(): IContextMutationResult<
  ITrackerGroupsSaveResponse,
  Error,
  ITrackerGroupsPayload
> {
  const { fetchPut, fetchPost } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<ITrackerGroupsSaveResponse, Error, ITrackerGroupsPayload>({
    mutationKey: [AFFILIATE_SAVE_TRACKER_GROUPS_MUTATION],
    mutationFn: ({ isEdit, ...payload }) => (
      isEdit
        ? fetchPut<ITrackerGroupsSaveResponse>(
            AFFILIATE_SAVE_TRACKER_GROUPS_API_URL,
            payload,
            {
              permission: AFFILIATES_TRACKER_GROUPS_PERMISSIONS.EDIT,
              urlParams: {
                id: payload.history_of_tracker_groups_change.id,
              },
            },
          )
        : fetchPost<ITrackerGroupsSaveResponse>(
            AFFILIATE_SAVE_TRACKER_GROUPS_API_URL,
            payload,
            {
              permission: AFFILIATES_TRACKER_GROUPS_PERMISSIONS.CREATE,
              urlParams: {
                id: '',
              },
            },
          )
    ),
    async onSuccess({ queryClient, variables, data }) {
      queryClient.setQueriesData<ITrackerGroupsFilterResponse>(
        { queryKey: [AFFILIATE_SHOW_TRACKER_GROUP_ITEM_QUERY] },
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            expected_action: data.expected_action,
            message: data.message,
            history_of_tracker_groups_change: data.history_of_tracker_groups_change,
          };
        },
      );

      queryClient.setQueriesData<IHistoryOfTrackerGroupsChanges>(
        { queryKey: [AFFILIATE_SHOW_HISTORY_TRACKER_GROUPS_QUERY] },
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            history_of_tracker_groups_changes: data.history_of_tracker_groups_changes,
          };
        },
      );

      displayPresetToast(variables.isEdit ? 'edited' : 'created');
    },
  });
}
